import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Badge, Button, Fab, Tooltip, makeStyles } from '@material-ui/core';
import { HeadsetMic } from '@material-ui/icons';
import { useSnackbar } from 'notistack';

import { ApiFailure, getLastFailure, subscribeToFailure } from 'apiFailures';
import { USER_OPERATIONS } from 'helper';
import { RootState } from 'interfaces';
import SupportDrawer from './SupportDrawer';

// Пачка сбоев и фоновые запросы при недоступном сервере — одно предложение сообщить, а не поток.
const OFFER_PAUSE_MS = 60000;
const OFFER_DURATION_MS = 10000;

const useStyles = makeStyles((theme) => ({
  fab: {
    position: 'fixed',
    right: theme.spacing(2.5),
    bottom: theme.spacing(2.5),
    zIndex: theme.zIndex.speedDial,
  },
  // Угол занят кнопкой AI-ассистента: она кончается на 84 px от низа, ось кнопок общая.
  aboveAssistant: {
    bottom: 92,
  },
}));

interface IReportActionProps {
  onClick(): void;
}

const ReportAction = ({ onClick }: IReportActionProps) => (
  <Button color="inherit" size="small" onClick={onClick}>
    Сообщить
  </Button>
);

/**
 * Канал техподдержки: кнопка на всех страницах, панель с формой обращения и перепиской,
 * предложение сообщить о сбое после отказа API.
 */
const Support = () => {
  const classes = useStyles();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const hasAssistant = useSelector((state: RootState) => state.operations.includes(USER_OPERATIONS.AI_VIEW));
  const [isOpen, setIsOpen] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(getLastFailure);
  const isOpenRef = useRef(isOpen);
  const offeredAtRef = useRef(0);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    setFailure(getLastFailure());

    return subscribeToFailure((next) => {
      setFailure(next);

      if (!next || isOpenRef.current || Date.now() - offeredAtRef.current < OFFER_PAUSE_MS) return;

      offeredAtRef.current = Date.now();
      const key = enqueueSnackbar('Произошёл сбой. Сообщить в поддержку?', {
        variant: 'info',
        autoHideDuration: OFFER_DURATION_MS,
        action: (
          <ReportAction
            onClick={() => {
              closeSnackbar(key);
              setIsOpen(true);
            }}
          />
        ),
      });
    });
  }, [enqueueSnackbar, closeSnackbar]);

  return (
    <>
      <Tooltip title="Сообщить об ошибке" placement="left">
        <Fab
          color="primary"
          aria-label={failure ? 'Техподдержка: есть сбой, о котором можно сообщить' : 'Техподдержка'}
          className={hasAssistant ? `${classes.fab} ${classes.aboveAssistant}` : classes.fab}
          onClick={() => setIsOpen(true)}
        >
          <Badge color="error" badgeContent="!" invisible={!failure}>
            <HeadsetMic />
          </Badge>
        </Fab>
      </Tooltip>

      <SupportDrawer open={isOpen} failure={failure} onClose={() => setIsOpen(false)} />
    </>
  );
};

export default Support;
